// ts-rest contract for the admin System Status page (SEP-23).
//
// One read endpoint returning a component-per-integration health board:
// database, ingestion pipelines (Numonix / transcription), the price-refresh
// cache, the agent runtime, the five custodian SFTP feeds, COAC + valuation
// uploads, Microsoft Graph, logo.dev, and the workflow scheduler. Every
// component is derived from a real persisted signal — there are no
// fabricated heartbeats (a source with no honest signal is excluded).
//
// Admin-gated server-side (@Persona('admin')). It has no page of its own: the
// public `/status` page reads the same board through `status.ts` and keeps
// only the coarse state word of each component group.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const systemStatusComponentSchema = z.object({
    /** Stable component key, e.g. 'database', 'numonix_ingestion', 'rbc'. */
    key: z.string(),
    /** Display label, e.g. 'Database', 'Numonix call ingestion'. */
    label: z.string(),
    /**
     * ok           — the source is configured and its latest signal is healthy.
     * degraded     — configured but the latest signal is stale / carries failures.
     * down         — the source is unreachable (database probe only, today).
     * unconfigured — no credentials / no data ever ingested for this source.
     */
    status: z.enum(['ok', 'degraded', 'down', 'unconfigured']),
    /** One honest sentence describing the current state. */
    detail: z.string(),
    /** ISO timestamp of the newest persisted event for this source, if any. */
    lastEventAt: z.string().nullable(),
    /** Small labelled figures rendered as chips (counts, cadences, flags). */
    metrics: z.array(z.object({ label: z.string(), value: z.string() })),
});
export type SystemStatusComponentDto = z.infer<typeof systemStatusComponentSchema>;

export const systemStatusResponseSchema = z.object({
    generatedAt: z.string(),
    components: z.array(systemStatusComponentSchema),
});
export type SystemStatusResponse = z.infer<typeof systemStatusResponseSchema>;

export const systemStatusContract = c.router(
    {
        getSystemStatus: {
            method: 'GET',
            path: '/system-status',
            responses: {
                200: systemStatusResponseSchema,
            },
            summary:
                'Component-per-integration health board for the admin System Status page (SEP-23). Persona-gated to admin.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
